import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Copy, RefreshCw, Trash2, Users, Sparkles, ExternalLink, Inbox, Crown, ShieldCheck, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../lib/usePlan';
import { supabase } from '../lib/supabaseClient';
import { products } from '../stripe-config';
import { redirectToCheckout } from '../lib/stripe';
import { useDocumentMeta } from '../lib/useDocumentMeta';

interface Member {
  team_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member';
  billing_source: 'team_seat' | 'personal_pro';
  joined_at: string;
  full_name?: string | null;
}

interface Invite {
  id: string;
  token: string;
  expires_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
  redemption_count: number;
}

interface OverflowRequest {
  id: string;
  user_id: string;
  created_at: string;
  resolved_at: string | null;
  outcome: string | null;
  full_name?: string | null;
}

export default function TeamPage() {
  const { user } = useAuth();
  const { activeTeam, teams, reload } = usePlan();
  const [members, setMembers] = useState<Member[]>([]);
  const [invite, setInvite] = useState<Invite | null>(null);
  const [overflows, setOverflows] = useState<OverflowRequest[]>([]);
  const [renaming, setRenaming] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  useDocumentMeta({ title: 'Team — PromptGPT', description: 'Manage your team plan' });

  const teamId = activeTeam?.team_id;
  const isOwner = activeTeam?.role === 'owner';
  const isAdmin = isOwner || activeTeam?.role === 'admin';

  const loadAll = useCallback(async () => {
    if (!teamId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const { data: membersData } = await supabase
      .from('team_members')
      .select('team_id, user_id, role, billing_source, joined_at')
      .eq('team_id', teamId);

    const { data: profilesData } = membersData
      ? await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', membersData.map((m: { user_id: string }) => m.user_id))
      : { data: [] as { id: string; full_name: string }[] };

    const profileMap = new Map<string, string>();
    (profilesData || []).forEach((p: { id: string; full_name: string }) => {
      profileMap.set(p.id, p.full_name);
    });

    setMembers(
      ((membersData as Member[]) || []).map((m) => ({
        ...m,
        full_name: profileMap.get(m.user_id) || null,
      })),
    );

    const { data: inviteData } = await supabase
      .from('team_invites')
      .select('id, token, expires_at, revoked_at, last_used_at, redemption_count')
      .eq('team_id', teamId)
      .is('revoked_at', null)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    setInvite((inviteData as Invite) || null);

    const { data: overflowData } = await supabase
      .from('team_overflow_requests')
      .select('id, user_id, created_at, resolved_at, outcome')
      .eq('team_id', teamId)
      .is('resolved_at', null)
      .order('created_at', { ascending: false });

    if (overflowData && overflowData.length > 0) {
      const userIds = (overflowData as OverflowRequest[]).map((o) => o.user_id);
      const { data: opProfiles } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);
      const opMap = new Map<string, string>();
      (opProfiles || []).forEach((p: { id: string; full_name: string }) => opMap.set(p.id, p.full_name));
      setOverflows(
        (overflowData as OverflowRequest[]).map((o) => ({ ...o, full_name: opMap.get(o.user_id) || null })),
      );
    } else {
      setOverflows([]);
    }

    const { data: teamRow } = await supabase
      .from('teams')
      .select('name')
      .eq('id', teamId)
      .maybeSingle();
    if (teamRow?.name) setTeamName(teamRow.name);

    setLoading(false);
  }, [teamId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const copyInvite = async () => {
    if (!invite) return;
    const url = `${window.location.origin}/team/join/${invite.token}`;
    await navigator.clipboard.writeText(url);
    toast.success('Invite link copied');
  };

  const rotate = async () => {
    if (!teamId) return;
    const { error } = await supabase.rpc('rotate_team_invite', { p_team_id: teamId });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success('New invite link generated');
    await loadAll();
  };

  const removeMember = async (m: Member) => {
    if (!teamId) return;
    if (m.role === 'owner') {
      toast.error('Cannot remove the team owner');
      return;
    }
    if (!confirm(`Remove ${m.full_name || 'this member'} from the team?`)) return;
    const { error } = await supabase
      .from('team_members')
      .delete()
      .eq('team_id', teamId)
      .eq('user_id', m.user_id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success('Member removed');
    await loadAll();
  };

  const renameTeam = async () => {
    if (!teamId) return;
    const name = teamName.trim();
    if (name.length < 2) return;
    const { error } = await supabase.from('teams').update({ name }).eq('id', teamId);
    if (error) {
      toast.error(error.message);
      return;
    }
    setRenaming(false);
    toast.success('Team renamed');
    await reload();
  };

  const dismissOverflow = async (id: string) => {
    const { error } = await supabase
      .from('team_overflow_requests')
      .update({ resolved_at: new Date().toISOString(), outcome: 'dismissed' })
      .eq('id', id);
    if (error) {
      toast.error(error.message);
      return;
    }
    await loadAll();
  };

  const addSecondTeam = async () => {
    if (!isOwner) return;
    setAdding(true);
    try {
      await redirectToCheckout(products.team.priceId, 'subscription', {
        team_name: `${teamName} (Sibling)`,
        success_url: `${window.location.origin}/team?welcome=1`,
        cancel_url: `${window.location.origin}/team`,
      });
    } catch (e) {
      toast.error((e as Error).message);
      setAdding(false);
    }
  };

  if (!user) return null;

  if (!teamId && !loading) {
    return (
      <div className="container-app py-16">
        <div className="max-w-xl mx-auto bg-white border border-ink-100 rounded-2xl p-10 text-center shadow-soft">
          <div className="w-12 h-12 rounded-full bg-accent-100 grid place-items-center mx-auto mb-4">
            <Users className="w-6 h-6 text-accent-700" />
          </div>
          <h1 className="font-display text-3xl mb-2">No team yet</h1>
          <p className="text-ink-600 mb-6">
            Start a Team plan to learn alongside up to ten people, with a shared workspace for notes,
            prompts, and lessons.
          </p>
          <Link to="/pricing" className="btn-primary inline-flex">
            See team pricing <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const seatLimit = activeTeam?.seat_limit ?? 10;
  const teamSeatCount = members.filter((m) => m.billing_source === 'team_seat').length;
  const renewalDate = activeTeam?.current_period_end
    ? new Date(activeTeam.current_period_end * 1000).toLocaleDateString()
    : null;

  return (
    <div className="container-app py-10 md:py-14 max-w-5xl">
      <div className="flex items-center gap-2 text-sm text-ink-500 mb-6">
        <Link to="/learn" className="hover:text-ink-700">Today</Link>
        <span>/</span>
        <span className="text-ink-700">Team</span>
      </div>

      <div className="bg-white border border-ink-100 rounded-2xl shadow-soft p-6 md:p-8 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs uppercase tracking-wider text-accent-700 font-semibold">Team</span>
              <span className="chip text-[10px]">{activeTeam?.sub_status || 'pending'}</span>
            </div>
            {renaming && isOwner ? (
              <div className="flex gap-2">
                <input
                  autoFocus
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="font-display text-3xl px-2 py-1 border border-ink-200 rounded-lg w-full max-w-md"
                />
                <button onClick={renameTeam} className="btn-primary">Save</button>
                <button onClick={() => setRenaming(false)} className="text-ink-500 hover:text-ink-700 px-3">
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => isOwner && setRenaming(true)}
                className="font-display text-3xl md:text-4xl text-left hover:text-accent-700 transition"
                disabled={!isOwner}
              >
                {teamName || activeTeam?.team_name || 'My Team'}
              </button>
            )}
            <p className="text-ink-600 text-sm mt-2">
              {teamSeatCount} of {seatLimit} seats used
              {renewalDate && <span className="text-ink-400"> · Renews {renewalDate}</span>}
              {activeTeam?.cancel_at_period_end && (
                <span className="text-coral-600 ml-2">· Cancels at period end</span>
              )}
            </p>
          </div>
          {teams.length > 1 && (
            <select
              className="text-sm border border-ink-200 rounded-lg px-3 py-2"
              value={activeTeam?.team_id}
              onChange={async (e) => {
                await supabase
                  .from('profiles')
                  .update({ current_team_id: e.target.value })
                  .eq('id', user.id);
                await reload();
              }}
            >
              {teams.map((t) => (
                <option key={t.team_id} value={t.team_id}>
                  {t.team_name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/team/workspace" className="btn-primary">
            <Sparkles className="w-4 h-4" /> Open shared workspace
          </Link>
          {isOwner && (
            <Link
              to="/settings"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ink-200 text-ink-700 hover:bg-warm-50 text-sm"
            >
              <ExternalLink className="w-4 h-4" /> Manage billing
            </Link>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="bg-white border border-ink-100 rounded-2xl shadow-soft p-6 md:p-8 mb-6">
          <h2 className="font-display text-xl mb-1">Invite link</h2>
          <p className="text-sm text-ink-600 mb-4">
            One reusable link. Share it in Slack, text, or anywhere else. Rotate to invalidate everyone who hasn't joined yet.
          </p>
          {invite ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 bg-warm-50 border border-ink-100 rounded-xl px-4 py-3 font-mono text-sm break-all">
                {`${window.location.origin}/team/join/${invite.token}`}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-ink-500">
                <span>Used {invite.redemption_count} time{invite.redemption_count === 1 ? '' : 's'}</span>
                {invite.last_used_at && (
                  <span>Last used {new Date(invite.last_used_at).toLocaleDateString()}</span>
                )}
                <span>Expires {new Date(invite.expires_at).toLocaleDateString()}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={copyInvite} className="btn-primary">
                  <Copy className="w-4 h-4" /> Copy link
                </button>
                <button
                  onClick={rotate}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ink-200 text-ink-700 hover:bg-warm-50 text-sm"
                >
                  <RefreshCw className="w-4 h-4" /> Rotate
                </button>
              </div>
            </div>
          ) : (
            <button onClick={rotate} className="btn-primary">
              <RefreshCw className="w-4 h-4" /> Generate invite link
            </button>
          )}
        </div>
      )}

      {isAdmin && overflows.length > 0 && (
        <div className="bg-sun-50 border border-sun-200 rounded-2xl p-6 md:p-8 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Inbox className="w-5 h-5 text-sun-700" />
            <h2 className="font-display text-xl">Overflow requests</h2>
          </div>
          <p className="text-sm text-ink-700 mb-4">
            Your team is at the {seatLimit}-seat limit. These people tried to join but were over the cap.
          </p>
          <ul className="space-y-3">
            {overflows.map((o) => (
              <li key={o.id} className="bg-white rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">{o.full_name || 'Unknown user'}</div>
                  <div className="text-xs text-ink-500">
                    Requested {new Date(o.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={addSecondTeam}
                    disabled={adding}
                    className="btn-primary text-sm disabled:opacity-50"
                  >
                    {adding ? 'Redirecting...' : 'Add second Team plan'}
                  </button>
                  <button
                    onClick={() => dismissOverflow(o.id)}
                    className="text-sm text-ink-500 hover:text-ink-700 px-3"
                  >
                    Dismiss
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white border border-ink-100 rounded-2xl shadow-soft p-6 md:p-8">
        <h2 className="font-display text-xl mb-4">Members</h2>
        {loading ? (
          <div className="text-sm text-ink-500">Loading...</div>
        ) : (
          <ul className="divide-y divide-ink-100">
            {members.map((m) => (
              <li key={m.user_id} className="py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-warm-100 grid place-items-center text-sm font-semibold text-ink-700">
                    {(m.full_name || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      {m.full_name || 'Unnamed'}
                      {m.role === 'owner' && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full">
                          <Crown className="w-3 h-3" /> Owner
                        </span>
                      )}
                      {m.role === 'admin' && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-warm-100 text-ink-700 px-2 py-0.5 rounded-full">
                          <ShieldCheck className="w-3 h-3" /> Admin
                        </span>
                      )}
                      {m.billing_source === 'personal_pro' && (
                        <span className="text-[10px] uppercase tracking-wider bg-mint-50 text-mint-700 px-2 py-0.5 rounded-full">
                          Personal Pro
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-ink-500">
                      Joined {new Date(m.joined_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                {isAdmin && m.role !== 'owner' && (
                  <button
                    onClick={() => removeMember(m)}
                    className="text-ink-400 hover:text-coral-600 p-2"
                    aria-label="Remove member"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
